import React, { useCallback, useContext, useEffect, useState } from 'react'
import ModulePanel from '../../ui/ModulePanel/ModulePanel'
import TabNav from '../../ui/TabNav/TabNav'
import StatusMessage, { initStatusMessage, useStatusMessage } from '../../ui/StatusMessage/StatusMessage'
import { AuthContext } from '../../App'
import { isCustomersListAllowedUser } from '../../config/customersAccessControl'
import { isMarketingOnlyUser } from '../../config/accessControl'
import listCustomers from '../../api/customers/listCustomers'
import CustomersFilters from './CustomersFilters'
import CustomersTable from './CustomersTable'
import {
  CUSTOMERS_NAME_SEARCH_FETCH_LIMIT,
  DEFAULT_CUSTOMERS_LIST_LIMIT,
  customerMatchesNameQuery,
  normalizeCustomerNameQuery,
  parseUaDateToMs,
} from './customersUiUtils'
import './Customers.sass'
import { OFFLINE_REGISTRATION_PATH } from '../../config/offlineRegistrationAccess'

const CUSTOMER_TABS = [
  { title: 'База клієнтів', path: '/customers' },
  { title: 'Офлайн-реєстрація клієнта', path: OFFLINE_REGISTRATION_PATH, target: '_blank' },
]

const EMPTY_FILTERS = {
  name: '',
  phone: '',
  createdFromDate: '',
  createdToDate: '',
  registrationChannel: '',
}

const buildListPayload = (filters, { cursor = null, limit = DEFAULT_CUSTOMERS_LIST_LIMIT } = {}) => {
  const payload = { limit }
  const nameRaw = typeof filters.name === 'string' ? filters.name.trim() : ''
  if (nameRaw) payload.name = nameRaw

  const phone = typeof filters.phone === 'string' ? filters.phone.trim() : ''
  if (phone) payload.phone = phone
  if (filters.registrationChannel) payload.registrationChannel = filters.registrationChannel

  const from = parseUaDateToMs(filters.createdFromDate, { endOfDay: false })
  const to = parseUaDateToMs(filters.createdToDate, { endOfDay: true })
  if (from != null) payload.createdFrom = from
  if (to != null) payload.createdTo = to
  if (cursor != null) payload.cursor = cursor
  return payload
}

function Customers() {
  const { user, setPageTitle } = useContext(AuthContext)
  const [statusMessage, setStatusMessage] = useStatusMessage()

  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS)
  const [formKey, setFormKey] = useState(0)

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const canAccess = Boolean(user?.uid) && !isMarketingOnlyUser(user.uid) && isCustomersListAllowedUser(user.uid)

  useEffect(() => {
    setPageTitle('Клієнти')
  }, [setPageTitle])

  const loadPage = useCallback(
    async ({ filters } = {}) => {
      setStatusMessage(initStatusMessage())
      setIsLoading(true)

      const nameQuery = normalizeCustomerNameQuery(filters.name)

      try {
        if (nameQuery) {
          const result = await listCustomers(
            buildListPayload(filters, {
              cursor: null,
              limit: CUSTOMERS_NAME_SEARCH_FETCH_LIMIT,
            })
          )

          if (!result.ok) {
            setStatusMessage({
              show: true,
              type: 'fail',
              message: result.message || 'Не вдалося завантажити клієнтів',
              closeAfter: 5000,
            })
            setRows([])
            setTotal(0)
            return
          }

          const items = (Array.isArray(result.data?.items) ? result.data.items : []).filter((item) =>
            customerMatchesNameQuery(item, nameQuery)
          )
          setRows(items)
          setTotal(items.length)
          return
        }

        let cursor = null
        let allItems = []
        let totalCount = 0

        do {
          const result = await listCustomers(
            buildListPayload(filters, {
              cursor,
              limit: DEFAULT_CUSTOMERS_LIST_LIMIT,
            })
          )

          if (!result.ok) {
            setStatusMessage({
              show: true,
              type: 'fail',
              message: result.message || 'Не вдалося завантажити клієнтів',
              closeAfter: 5000,
            })
            if (!allItems.length) {
              setRows([])
              setTotal(0)
            }
            return
          }

          const items = Array.isArray(result.data?.items) ? result.data.items : []
          allItems = allItems.concat(items)
          totalCount = typeof result.data?.total === 'number' ? result.data.total : allItems.length
          cursor = result.data?.nextCursor ?? null
        } while (cursor)

        setRows(allItems)
        setTotal(totalCount)
      } finally {
        setIsLoading(false)
      }
    },
    [setStatusMessage]
  )

  useEffect(() => {
    if (!canAccess) return undefined
    loadPage({ filters: appliedFilters })
    return undefined
    // Initial load when access is granted; Apply/Reset call loadPage explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess])

  if (!canAccess) {
    return (
      <div className="CustomersPage">
        <ModulePanel title="Клієнти">
          <p className="CustomersPage-Denied">Немає доступу до списку клієнтів.</p>
        </ModulePanel>
      </div>
    )
  }

  const onApply = (nextFilters = draftFilters) => {
    const next = { ...nextFilters }
    setDraftFilters(next)
    setAppliedFilters(next)
    loadPage({ filters: next })
  }

  const onReset = () => {
    const cleared = { ...EMPTY_FILTERS }
    setDraftFilters(cleared)
    setFormKey((key) => key + 1)
    setAppliedFilters(cleared)
    loadPage({ filters: cleared })
  }

  return (
    <div className="CustomersPage">
      <TabNav links={CUSTOMER_TABS} />
      {statusMessage.show ? (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      ) : null}
      <ModulePanel title={`Клієнти (${total})`}>
        <CustomersFilters
          filters={draftFilters}
          onChange={setDraftFilters}
          onApply={onApply}
          onReset={onReset}
          formKey={formKey}
        />
        <div className="CustomersPage-Divider" aria-hidden="true" />
        <CustomersTable rows={rows} isLoading={isLoading} />
      </ModulePanel>
    </div>
  )
}

export default Customers
